import { formatCurrency } from '@/lib/format'
import type { AreaSurcharge, AreaSurchargeEnd, LateChargeStatus, LatePricing } from '@/types/api'

/**
 * Words for a booking's Cairo area surcharge, shared by the chips, the detail
 * rows and the exports so every screen names a place the same way.
 */

/** "Nasr City · East Cairo", "Maadi · no group", or "Outside the sub-areas". */
export function describeEnd(end: AreaSurchargeEnd): string {
  if (!end.subArea) return 'Outside the sub-areas'
  return `${end.subArea} · ${end.group ?? 'no group'}`
}

/**
 * What one end added: "+E£80", or "no surcharge". A late end says what being
 * late added on top — "+E£80 · late +E£20", "late +E£80" in the primary group —
 * or "late, not charged" on a booking from an app that could not show the
 * late-night charge.
 */
export function describeEndCharge(end: AreaSurchargeEnd, lateStatus: LateChargeStatus | null = null): string {
  const byDay = end.surchargeMinor > 0 ? `+${formatCurrency(end.surcharge)}` : null
  if (!end.late) return byDay ?? 'no surcharge'
  const late =
    lateStatus === 'NOT_SHOWN'
      ? 'late, not charged'
      : end.lateChargeMinor > 0
        ? `late +${formatCurrency(end.lateCharge)}`
        : 'late, nothing added'
  return byDay ? `${byDay} · ${late}` : late
}

/** Whether a booking has an area chip or a late-night chip to show in a table row. */
export function hasAreaChips(area: AreaSurcharge): boolean {
  return (area.any && area.totalMinor > 0) || area.lateChargeMinor > 0 || area.lateStatus === 'NOT_SHOWN'
}

/** Which ends were late: "pickup and drop-off", "pickup", "drop-off", or an empty string. */
export function describeLateEnds(area: AreaSurcharge): string {
  if (area.pickup.late && area.dropoff.late) return 'pickup and drop-off'
  if (area.pickup.late) return 'pickup'
  if (area.dropoff.late) return 'drop-off'
  return ''
}

/** "between 23:00 and 04:00" — the window as a phrase, Cairo time. */
export function describeLateWindow(latePricing: Pick<LatePricing, 'start' | 'end'>): string {
  return `between ${latePricing.start} and ${latePricing.end}`
}

/**
 * The line an export cell carries, or an empty string when the booking's ends
 * fell in no sub-area. The server words it; the fallback is only for a
 * response that carries the ends without the summary.
 */
export function describeAreaSurcharge(area: AreaSurcharge | null | undefined): string {
  if (!area || !area.any) return ''
  return (
    area.summary ??
    `${describeEnd(area.pickup)} → ${describeEnd(area.dropoff)} · ${
      area.totalMinor > 0 ? `+${formatCurrency(area.total)}` : 'no surcharge'
    }`
  )
}

/**
 * The late-night charge an export cell carries: the amount when one was
 * charged, "not charged (older app)" when the booking's late ends went
 * uncharged, and an empty string when nothing was late.
 */
export function lateChargeExportValue(area: AreaSurcharge | null | undefined): number | string {
  if (!area) return ''
  if (area.lateStatus === 'NOT_SHOWN') return 'not charged (older app)'
  return area.lateChargeMinor > 0 ? area.lateCharge : ''
}
